/**
 * AudioReactiveTimeline — AudioReactiveCore run once over a decoded song and
 * kept as a timeline: what the music is doing at any moment, looked up by time.
 *
 * For a tool that draws over a FILE (the visualiser and the beat pulse of
 * /audio-to-video). The preview and every export lane ask the same timeline
 * about the same moment and get the same numbers: no AnalyserNode in the
 * preview, no emulation of one in the export, nothing that depends on the
 * frame rate, on the order the frames are rendered in, or on a seek.
 *
 *   var tl = AudioReactiveTimeline.forBuffer(buffer, { workerUrl: url });  // one per buffer
 *   tl.ready.then(function () { … });   // the whole song is analysed
 *   var f = tl.at(t, fps);               // drivers at t (one reused object — read, do not keep);
 *                                        // null while that part of the song is not analysed yet
 *
 * The analysis runs in a Worker (timeline-worker.js) fed 10-second mono
 * chunks — about 200× real time on a desktop, so the page never waits for
 * it; where a Worker cannot start, the same Builder runs on the page in short
 * slices. Storage: 14 scalars (Float32) + 32 band levels (bytes) per analysis
 * step (~86 a second at 44.1 kHz): ~1.5 MB for a 4-minute song, ~25 MB for an hour.
 *
 * What a frame at t shows. The detector reports a hit ~40 ms after the sound
 * starts (10 songs against librosa's backtracked onsets: median 35–46 ms), and
 * a frame stays on screen for 1/fps after its time. at(t, fps) reads the
 * timeline at t + DETECT_LAG_S + 0.5/fps, so a pulse lands on the frame during
 * which the hit starts (measured peak 17–30 ms after the onset at 24 fps;
 * sound ahead of the picture is noticed from ~45 ms, ITU-R BT.1359).
 * docs/why/primitives/audio-reactive-timeline.md.
 *
 * ES5, no DOM. Needs AudioReactiveCore (core.js) loaded first.
 */
(function (root) {
    'use strict';

    var BANDS = 32;
    /* per-step scalars, in this order */
    var KEYS = ['level', 'bass', 'mid', 'high', 'kick', 'snare', 'hat', 'onset', 'beat', 'beatLock', 'presence', 'intensity', 'bright', 'flow'];
    var NS = KEYS.length;
    var DETECT_LAG_S = 0.04;
    var CHUNK_S = 10;
    var STALL_MS = 20000;

    function Core() { return root.AudioReactiveCore; }

    /* ═══════════════════ Builder: samples in, steps out ═══════════════════ */

    /* The step loop of AVExport (public/js/audio-visualizer/export.js), made
       resumable across chunks: a window ends every hop samples, the first
       windows are zero-padded on the left, the last one ends at or before the
       last sample. */
    function Builder(sr, opts) {
        opts = opts || {};
        var C = Core();
        this.sr = sr;
        this.an = new C.Analyzer(sr);
        this.tr = new C.Tracker(sr, { analyzer: this.an, background: 'silence' });
        if (opts.sensitivity) this.tr.setSensitivity(opts.sensitivity);
        this.n = this.an.fftSize;
        this.hop = this.an.hop;
        this.frame = new Float64Array(C.FRAME_LEN);
        this.win = new Float32Array(this.n);
        this.tail = new Float32Array(0);   /* the last ≤ n samples already pushed */
        this.pushed = 0;                   /* samples pushed so far */
        this.next = this.hop;              /* end (exclusive, in samples) of the next window */
        this.beat = 0;
    }

    /* x: the next mono samples. Returns the steps whose windows now end inside
       what has been pushed: { n, sc: Float32Array(n·NS), sp: Uint8Array(n·BANDS) }. */
    Builder.prototype.push = function (x) {
        var n = this.n, hop = this.hop, tl = this.tail.length;
        var base = this.pushed - tl;               /* global index of buf[0] */
        var buf = new Float32Array(tl + x.length);
        buf.set(this.tail, 0); buf.set(x, tl);
        var total = this.pushed + x.length;
        var count = this.next <= total ? Math.floor((total - this.next) / hop) + 1 : 0;
        var sc = new Float32Array(count * NS), sp = new Uint8Array(count * BANDS);
        var an = this.an, tr = this.tr, frame = this.frame, win = this.win, C = Core();
        for (var s = 0; s < count; s++) {
            var end = this.next, w;
            if (end - n >= base) {
                w = buf.subarray(end - n - base, end - base);
            } else {
                /* the head of the song: zeros before sample 0 */
                win.fill(0);
                var from = Math.max(0, end - n);
                win.set(buf.subarray(from - base, end - base), n - (end - from));
                w = win;
            }
            an.analyze(w);
            an.writeFrame(end / this.sr, frame);
            var d = tr.step(frame), o = s * NS, k;
            sc[o] = d.level; sc[o + 1] = d.bass; sc[o + 2] = d.mid; sc[o + 3] = d.high;
            sc[o + 4] = d.kick; sc[o + 5] = d.snare; sc[o + 6] = d.hat; sc[o + 7] = d.onset;
            /* A predicted beat counts only when it falls on sound: the
               tracker keeps its tempo for 2 s and more after the music stops
               (so a break does not lose the beat), and the picture would go
               on pulsing in the silence. tr.presence is THIS step, not the
               smoothed driver — digital silence reads 0 at once. */
            var bt = this.beat * Math.exp(-(hop / this.sr) / C.PARAMS.BEAT_TAU);
            if (tr.beatEvent && tr.presence > 0.3 && bt < 1) bt = 1;
            this.beat = bt;
            sc[o + 8] = bt; sc[o + 9] = d.beatLock;
            sc[o + 10] = tr.presence;
            sc[o + 11] = d.intensity; sc[o + 12] = d.bright;
            /* integrated speed (Tracker flows): a phase that runs faster in
               loud music and creeps in quiet, never jumps */
            sc[o + 13] = d.flow;
            var sb = s * BANDS;
            for (k = 0; k < BANDS; k++) {
                var v = d.spectrum[k];
                sp[sb + k] = v <= 0 ? 0 : (v >= 1 ? 255 : Math.round(v * 255));
            }
            this.next += hop;
        }
        this.pushed = total;
        this.tail = buf.length > n ? buf.slice(buf.length - n) : buf;
        return { n: count, sc: sc, sp: sp };
    };

    /* ═══════════════════ Timeline: steps kept, looked up by time ═══════════════════ */

    function mono(buffer, from, to) {
        var ch = buffer.numberOfChannels, out = new Float32Array(to - from), c, i;
        for (c = 0; c < ch; c++) {
            var d = buffer.getChannelData(c);
            for (i = from; i < to; i++) out[i - from] += d[i];
        }
        if (ch > 1) for (i = 0; i < out.length; i++) out[i] /= ch;
        return out;
    }

    function Timeline(buffer, opts) {
        opts = opts || {};
        var C = Core();
        this.sr = buffer.sampleRate;
        this.length = buffer.length;
        this.duration = buffer.duration;
        this.hop = new C.Analyzer(this.sr).hop;
        this.total = Math.floor(this.length / this.hop);
        this.sc = new Float32Array(this.total * NS);
        this.sp = new Uint8Array(this.total * BANDS);
        this.count = 0;
        this.done = false;
        this.lane = '';
        this.ms = 0;
        this.failed = null;
        this._f = frame();
        var self = this;
        this.ready = new Promise(function (resolve, reject) { self._resolve = resolve; self._reject = reject; });
        /* nobody has to wait on it: a page that never awaits must not log an unhandled rejection */
        this.ready.catch(function () {});
        this._start(buffer, opts);
    }

    Timeline.prototype._take = function (r) {
        var room = this.total - this.count, n = Math.min(r.n, room);
        if (!this.firstMs && n > 0) this.firstMs = Math.round((root.performance ? root.performance.now() : Date.now()) - this._t0);
        if (n > 0) {
            this.sc.set(n === r.n ? r.sc : r.sc.subarray(0, n * NS), this.count * NS);
            this.sp.set(n === r.n ? r.sp : r.sp.subarray(0, n * BANDS), this.count * BANDS);
            this.count += n;
        }
    };

    Timeline.prototype._finish = function (t0) {
        this.done = true;
        this.count = this.total;
        this.ms = Math.round((root.performance ? root.performance.now() : Date.now()) - t0);
        this._buffer = null;
        this._resolve(this);
    };

    Timeline.prototype._start = function (buffer, opts) {
        var self = this, sr = this.sr, len = this.length, chunk = Math.round(CHUNK_S * sr);
        var t0 = root.performance ? root.performance.now() : Date.now();
        this._t0 = t0;
        this.firstMs = 0;
        this._buffer = buffer;
        if (!this.total) { this.lane = 'empty'; this._finish(t0); return; }

        function onPage() {
            /* the same Builder on the page: one second of song per slice, so a
               phone keeps drawing between slices */
            self.lane = 'page';
            self.count = 0;
            var b = new Builder(sr, opts), pos = 0, slice = sr;
            (function step() {
                if (self._cancelled) return;
                try {
                    var to = Math.min(len, pos + slice);
                    self._take(b.push(mono(buffer, pos, to)));
                    pos = to;
                } catch (e) { self.failed = e; self._reject(e); return; }
                if (pos >= len) { self._finish(t0); return; }
                setTimeout(step, 0);
            })();
        }

        var W = root.Worker, w = null;
        if (opts.workerUrl && typeof W === 'function') {
            try { w = new W(opts.workerUrl); } catch (e) { w = null; }
        }
        if (!w) { onPage(); return; }
        this.lane = 'worker';
        this._worker = w;
        var pos = 0, fellBack = false, unarm = null;
        /* a worker that stops answering (killed, frozen past a resume) hands
           the song to the page; the clock counts only visible time — a hidden
           tab's worker is allowed to be slow */
        function arm() {
            if (unarm) unarm();
            var vt = root.__visibleTimeout;
            if (typeof vt === 'function') unarm = vt(fallBack, STALL_MS);
            else { var h = setTimeout(fallBack, STALL_MS); unarm = function () { clearTimeout(h); }; }
        }
        function fallBack() {
            if (unarm) { unarm(); unarm = null; }
            if (fellBack || self.done) return;
            fellBack = true;
            try { w.terminate(); } catch (e) {}
            self._worker = null;
            onPage();
        }
        function send() {
            var to = Math.min(len, pos + chunk), x = mono(buffer, pos, to);
            pos = to;
            arm();
            w.postMessage({ cmd: 'chunk', x: x }, [x.buffer]);
        }
        w.onerror = function (e) { if (e && e.preventDefault) e.preventDefault(); fallBack(); };
        w.onmessage = function (e) {
            var m = e.data || {};
            if (self._cancelled) return;
            if (m.cmd === 'ready') {
                if (m.hop !== self.hop) { fallBack(); return; }
                send();
            } else if (m.cmd === 'steps') {
                self._take(m);
                if (pos >= len) {
                    if (unarm) { unarm(); unarm = null; }
                    try { w.terminate(); } catch (e2) {}
                    self._worker = null;
                    self._finish(t0);
                } else send();
            } else if (m.cmd === 'error') {
                fallBack();
            }
        };
        arm();
        w.postMessage({ cmd: 'init', sr: sr, sensitivity: opts.sensitivity || 1 });
    };

    Timeline.prototype.release = function () {
        this._cancelled = true;
        if (!this.done) this._reject(new Error('released'));
        if (this._worker) { try { this._worker.terminate(); } catch (e) {} this._worker = null; }
        this._buffer = null;
    };

    /* Seconds of the song analysed so far. */
    Timeline.prototype.coverage = function () {
        return this.done ? this.duration : this.count * this.hop / this.sr;
    };

    /* The drivers at time t of a picture shown at fps frames a second (0 =
       the moment itself), into `out` (an object from frame()) or one reused
       object; null while t is not analysed. */
    Timeline.prototype.at = function (t, fps, out) {
        var f = out || this._f, i, k;
        var tt = t + DETECT_LAG_S + (fps > 0 ? 0.5 / fps : 0);
        /* step s ends at (s + 1)·hop */
        var x = tt * this.sr / this.hop - 1;
        if (!(x >= 0) || !this.total) {
            for (i = 0; i < NS; i++) f[KEYS[i]] = 0;
            for (k = 0; k < BANDS; k++) f.spectrum[k] = 0;
            return f;
        }
        var s = Math.floor(x), a = x - s;
        if (s >= this.total - 1) { s = this.total - 1; a = 0; }
        if (s + (a > 0 ? 1 : 0) >= this.count) return null;
        var o0 = s * NS, o1 = a > 0 ? o0 + NS : o0, sc = this.sc;
        for (i = 0; i < NS; i++) f[KEYS[i]] = sc[o0 + i] + (sc[o1 + i] - sc[o0 + i]) * a;
        var b0 = s * BANDS, b1 = a > 0 ? b0 + BANDS : b0, sp = this.sp;
        for (k = 0; k < BANDS; k++) f.spectrum[k] = (sp[b0 + k] + (sp[b1 + k] - sp[b0 + k]) * a) / 255;
        return f;
    };

    /* The 32 band levels resampled to n points across [lo, hi] of the band
       axis (0 = 30 Hz, 1 = 16 kHz, log spaced), Catmull-Rom between bands. */
    function resample(spectrum, n, out, lo, hi) {
        out = out && out.length >= n ? out : new Float32Array(n);
        lo = lo == null ? 0 : lo; hi = hi == null ? 1 : hi;
        var last = BANDS - 1;
        for (var i = 0; i < n; i++) {
            var u = n > 1 ? lo + (hi - lo) * i / (n - 1) : (lo + hi) / 2;
            var x = u * last, j = Math.floor(x), a = x - j;
            if (j >= last) { out[i] = spectrum[last]; continue; }
            var p0 = spectrum[j > 0 ? j - 1 : 0], p1 = spectrum[j], p2 = spectrum[j + 1], p3 = spectrum[j + 2 <= last ? j + 2 : last];
            var v = 0.5 * ((2 * p1) + (-p0 + p2) * a + (2 * p0 - 5 * p1 + 4 * p2 - p3) * a * a + (-p0 + 3 * p1 - 3 * p2 + p3) * a * a * a);
            out[i] = v < 0 ? 0 : (v > 1 ? 1 : v);
        }
        return out;
    }

    /* An empty drivers object: every key 0, 32 silent bands. */
    function frame() {
        var f = { spectrum: new Float32Array(BANDS) };
        for (var i = 0; i < NS; i++) f[KEYS[i]] = 0;
        return f;
    }

    var _byBuffer = typeof WeakMap === 'function' ? new WeakMap() : null;

    root.AudioReactiveTimeline = {
        KEYS: KEYS,
        BANDS: BANDS,
        DETECT_LAG_S: DETECT_LAG_S,
        Builder: Builder,
        Timeline: Timeline,
        resample: resample,
        frame: frame,
        /* One timeline per decoded buffer: the live state and a frozen export
           snapshot hold the same buffer, so they read the same analysis. */
        forBuffer: function (buffer, opts) {
            if (!buffer || !buffer.length || !Core()) return null;
            /* a failed one stays failed: the same song fails the same way */
            var tl = _byBuffer && _byBuffer.get(buffer);
            if (tl) return tl;
            tl = new Timeline(buffer, opts);
            if (_byBuffer) _byBuffer.set(buffer, tl);
            return tl;
        },
        peek: function (buffer) { return (buffer && _byBuffer && _byBuffer.get(buffer)) || null; }
    };
})(typeof globalThis !== 'undefined' ? globalThis : (typeof self !== 'undefined' ? self : this));
